/**
 * PageTabs — the 32px tab strip under a page header.
 *
 * The bottom hairline is an INSET box-shadow, not a border: a real `border-b`
 * adds a layout pixel and sits 1px low, whereas the inset shadow stays flush.
 * Selected/hover = blue text + a 3px bottom bar.
 *
 * `count` is a neutral quantity chip ("12 rows"). `badge` is an ALERT — red by
 * default, for "something behind this tab needs attention now". They are
 * deliberately different things; don't merge them.
 */
import { type Component, For, Show, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import { cn } from "./utils/cn";
import { focusRing, focusRingInset } from "./utils/focus-ring";
import { CountChip } from "./lozenge";

export interface TabDef<T extends string = string> {
  id: T;
  label: string;
  count?: number;
  badge?: string;
  badgeTone?: "danger" | "info";
}

export function PageTabs<T extends string>(props: {
  tabs: TabDef<T>[];
  active: T;
  onChange: (id: T) => void;
  class?: string;
}): JSX.Element {
  return (
    <nav
      aria-label="Page sections"
      class={cn("shadow-[inset_0_-1px_0_0_var(--c9-border)]", props.class)}
    >
      {/* The tablist is inset to the page's gutter (px-200 = 16px) so the tabs
          align with the content and never sit flush against the side-nav border;
          the hairline above still spans the full width. */}
      <div role="tablist" class="flex min-w-0 items-stretch gap-025 overflow-x-auto px-200">
        <For each={props.tabs}>
        {(tab) => {
          const selected = () => tab.id === props.active;
          return (
            <button
              type="button"
              role="tab"
              aria-selected={selected()}
              onClick={() => props.onChange(tab.id)}
              class={cn(
                "relative flex h-8 shrink-0 items-center gap-075 whitespace-nowrap",
                "pl-075 pr-[10px] text-body font-[570] transition-colors",
                selected()
                  ? "text-[color:var(--c9-text-selected)]"
                  : "text-[color:var(--c9-text-subtle)] hover:text-[color:var(--c9-text-selected)]",
                focusRingInset,
              )}
            >
              {tab.label}
              <Show when={tab.count !== undefined}>
                <CountChip value={tab.count as number} />
              </Show>
              <Show when={tab.badge}>
                {(badge) => (
                  <span
                    data-tab-badge={tab.id}
                    class={cn(
                      "inline-flex min-w-[16px] items-center justify-center rounded-full px-[5px]",
                      "text-[11px] font-[670] leading-[16px] tabular-nums",
                      tab.badgeTone === "info"
                        ? "bg-[var(--c9-bg-information)] text-[color:var(--c9-text-information)]"
                        : "bg-[var(--c9-bg-danger)] text-[color:var(--c9-text-danger)]",
                    )}
                  >
                    {badge()}
                  </span>
                )}
              </Show>
              <Show when={selected()}>
                <span
                  aria-hidden="true"
                  class="absolute inset-x-[6px] bottom-0 h-[3px] bg-[color:var(--c9-border-selected)]"
                />
              </Show>
            </button>
          );
        }}
        </For>
      </div>
    </nav>
  );
}

type IconComponent = Component<{ size?: number | string; class?: string }>;

/**
 * Tabs — generic, state-driven in-content tabs (the in-page sibling of the
 * header-level `PageTabs`). Same anatomy: a 32px row on a full-width hairline,
 * selected = blue text/icon + a 3px bottom bar. Controlled via `value`/`onChange`.
 * Use inside a card or section to switch panels.
 */
export interface Tab {
  id: string;
  label: string;
  icon?: IconComponent;
  count?: number;
}

export function Tabs(props: {
  tabs: Tab[];
  value: string;
  onChange: (id: string) => void;
  ariaLabel?: string;
  class?: string;
}): JSX.Element {
  return (
    <nav
      aria-label={props.ariaLabel ?? "Tabs"}
      role="tablist"
      class={cn(
        "flex items-stretch gap-050 overflow-x-auto",
        "shadow-[inset_0_-1px_0_0_var(--c9-border)]",
        props.class,
      )}
    >
      <For each={props.tabs}>
        {(tab) => {
          const selected = (): boolean => props.value === tab.id;
          return (
            <button
              type="button"
              role="tab"
              aria-selected={selected()}
              onClick={() => props.onChange(tab.id)}
              class={cn(
                "group relative flex h-8 shrink-0 items-center gap-075 rounded-small px-100",
                "text-body font-[570] transition-colors",
                selected()
                  ? "text-[color:var(--c9-text-selected)]"
                  : "text-[color:var(--c9-text-subtle)] hover:text-[color:var(--c9-text-selected)]",
                focusRing,
              )}
            >
              <Show when={tab.icon}>
                <span
                  class={cn(
                    "shrink-0",
                    selected()
                      ? "text-[color:var(--c9-text-selected)]"
                      : "text-[color:var(--c9-icon-subtle)] group-hover:text-[color:var(--c9-text-selected)]",
                  )}
                >
                  <Dynamic component={tab.icon!} size={16} />
                </span>
              </Show>
              <span class="whitespace-nowrap">{tab.label}</span>
              <Show when={tab.count !== undefined}>
                <CountChip value={tab.count as number} />
              </Show>
              <span
                aria-hidden="true"
                class={cn(
                  "absolute inset-x-050 bottom-0 h-[3px] bg-[color:var(--c9-border-selected)] transition-opacity",
                  selected() ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                )}
              />
            </button>
          );
        }}
      </For>
    </nav>
  );
}
